import React, { useState, useEffect } from 'react';
import { DraggablePanel } from '../ui/DraggablePanel';
import { useModelStore } from '../../store/useModelStore';

interface DraggableCoordinateInputModalProps {
  isOpen: boolean;
  nodeId: string | null;
  onClose: () => void;
}

/**
 * Draggable Coordinate Input Modal
 * Allows users to edit node coordinates in a draggable panel
 */
export const DraggableCoordinateInputModal: React.FC<DraggableCoordinateInputModalProps> = ({
  isOpen,
  nodeId,
  onClose
}) => {
  const modelStore = useModelStore();
  const [coordinates, setCoordinates] = useState({ x: '0', y: '0', z: '0' });
  const [error, setError] = useState<string | null>(null);

  // Get current node data
  const currentNode = nodeId ? modelStore?.nodes?.find((n: any) => n.id === nodeId) : null;

  // Initialize coordinates when modal opens or node changes
  useEffect(() => {
    if (isOpen && currentNode) {
      setCoordinates({
        x: currentNode.x.toString(),
        y: currentNode.y.toString(),
        z: currentNode.z.toString()
      });
      setError(null);
    }
  }, [isOpen, currentNode]);

  const handleInputChange = (axis: 'x' | 'y' | 'z', value: string) => {
    // Allow empty string, numbers, negative numbers, and decimals
    if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
      setCoordinates(prev => ({ ...prev, [axis]: value }));
      setError(null);
    }
  };

  const validateAndUpdateNode = () => {
    if (!currentNode || !modelStore?.updateNode) {
      setError('Node not found or update function unavailable');
      return;
    }

    // Parse coordinates
    const x = parseFloat(coordinates.x);
    const y = parseFloat(coordinates.y);
    const z = parseFloat(coordinates.z);

    // Validate coordinates
    if (isNaN(x) || isNaN(y) || isNaN(z)) {
      setError('Please enter valid numeric coordinates');
      return;
    }

    // Check for reasonable bounds
    if (Math.abs(x) > 10000 || Math.abs(y) > 10000 || Math.abs(z) > 10000) {
      setError('Coordinates must be between -10000 and +10000');
      return;
    }

    // Check for duplicate coordinates (excluding current node)
    const isDuplicate = modelStore.nodes?.some((node: any) => 
      node.id !== nodeId &&
      Math.abs(node.x - x) < 0.001 && 
      Math.abs(node.y - y) < 0.001 && 
      Math.abs(node.z - z) < 0.001
    );

    if (isDuplicate) {
      setError('A node already exists at these coordinates');
      return;
    }

    // Update the node
    try {
      modelStore.updateNode(nodeId!, { x, y, z });
      console.log(`✅ Node ${nodeId} updated to coordinates:`, { x, y, z });
      onClose();
    } catch (err) {
      setError('Failed to update node position');
      console.error('Error updating node:', err);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !error) {
      validateAndUpdateNode();
    }
  };

  if (!isOpen || !nodeId) return null;

  // Calculate initial position (center of screen)
  const initialPosition = {
    x: Math.max(50, (window.innerWidth - 400) / 2),
    y: Math.max(50, (window.innerHeight - 300) / 2)
  };

  return (
    <DraggablePanel
      initialPosition={initialPosition}
      title={`Edit Node Coordinates - Node ${nodeId}`}
      onClose={onClose}
      style={{
        width: '400px',
        maxWidth: '90vw'
      }}
    >
      <div className="p-6">
        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Current position: ({currentNode?.x.toFixed(3)}, {currentNode?.y.toFixed(3)}, {currentNode?.z.toFixed(3)})
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            Enter new precise coordinates below:
          </p>
        </div>
        
        <div className="space-y-4">
          {(['x', 'y', 'z'] as const).map((axis) => (
            <div key={axis}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {axis.toUpperCase()} Coordinate (m):
              </label>
              <input
                type="text"
                value={coordinates[axis]}
                onChange={(e) => handleInputChange(axis, e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={`Enter ${axis.toUpperCase()} coordinate`}
              />
            </div>
          ))}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={validateAndUpdateNode}
              disabled={!!error}
              className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md transition-colors"
            >
              Update Position
            </button>
          </div>
        </div>
      </div>

      {/* Footer with instructions */}
      <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-b-lg">
        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
          🖱️ Drag the title bar to move • 📐 Enter coordinates and press Update Position
        </div>
      </div>
    </DraggablePanel>
  );
};

export default DraggableCoordinateInputModal;
